import {
  CompletedTransactions,
  MergedScheduledPayment,
  ScheduledPayment,
  ScheduledPaymentCompletedTransaction,
  ScheduledPaymentUpcomingTransaction,
  ScheduledTransactionsList,
  TransactionStatus,
  UpcomingTransactions,
} from './VoPayApi.types';

// Legacy records store the old frequency vocabulary; normalize the values that
// have a /scheduled-payment equivalent so consumers only deal with one
// vocabulary ('bimonthly' and 'single' have none and pass through as-is).
function toRepeats(nameOfFrequency: ScheduledTransactionsList['NameOfFrequency']): MergedScheduledPayment['Repeats'] {
  switch (nameOfFrequency) {
    case 'biweekly':
      return 'bi-weekly';
    case '3 months':
      return 'quarterly';
    case '6 months':
      return 'semi-annually';
    case 'yearly':
      return 'annually';
    default:
      return nameOfFrequency;
  }
}

// VIOEftScheduledPayments.Status is ENUM('in progress', 'cancelled',
// 'completed', 'pending approval'); anything unexpected counts as running.
function toScheduledPaymentStatus(status: string): MergedScheduledPayment['Status'] {
  return status === 'cancelled' || status === 'completed' || status === 'pending approval' ? status : 'in progress';
}

function toTransactionStatus(status: string): ScheduledPaymentCompletedTransaction['Status'] {
  if (status === 'upcoming' || status === 'completed') return status;

  return Object.values(TransactionStatus).find((knownStatus) => knownStatus === status) ?? TransactionStatus.BLANK;
}

function toCompletedTransaction(transaction: CompletedTransactions): ScheduledPaymentCompletedTransaction {
  return {
    ID: transaction.ID,
    TransactionID: transaction.TransactionID,
    ScheduledDate: transaction.ScheduledDate,
    LastModified: transaction.LastModified,
    Status: toTransactionStatus(transaction.Status),
    Amount: transaction.Amount,
  };
}

function toUpcomingTransaction(transaction: UpcomingTransactions): ScheduledPaymentUpcomingTransaction {
  return {
    ...toCompletedTransaction(transaction),
    ScheduledDateTime: transaction.ScheduledDate,
  };
}

export function toMergedScheduledPayment(payment: ScheduledPayment): MergedScheduledPayment {
  return { ...payment, IsLegacy: false };
}

/**
 * Maps a legacy /eft|ach/scheduled-transactions record (one that predates the
 * VIOScheduledPayments table, i.e. carries no TransactionType) into the
 * /scheduled-payment shape so both sources render through the same components.
 */
export function legacyToMergedScheduledPayment(
  transaction: ScheduledTransactionsList,
  country: string,
): MergedScheduledPayment {
  const isFund = transaction.Type === 'fund';
  const details = transaction.ScheduledDetails;
  const fullName = details.CompanyName || [details.FirstName, details.LastName].filter(Boolean).join(' ');

  return {
    ID: transaction.ID,
    ClientAccountID: '',
    FullName: fullName,
    Amount: transaction.Amount,
    Currency: transaction.Currency,
    PaymentType: isFund ? 'debit' : 'credit',
    TransactionType:
      country === 'US' ? (isFund ? 'ACH Funding' : 'ACH Withdrawal') : isFund ? 'EFT Funding' : 'EFT Withdrawal',
    Token: details.Token,
    ClientReferenceNumber: details.ClientReferenceNumber,
    Notes: '',
    Description: transaction.Description,
    Status: toScheduledPaymentStatus(transaction.Status),
    StartDateTime: transaction.StartDate,
    ScheduleEndDate: transaction.EndDate || null,
    NumberOfPayments: transaction.EndingAfterPayments,
    ScheduleType: transaction.Frequency === 'recurring' ? 'recurring' : 'single',
    Repeats: toRepeats(transaction.NameOfFrequency),
    GLCode: '',
    TokenData: {
      Type: 'bank',
      FullName: fullName,
      MaskedAccount: details.AccountNumber,
      Bank: details.FinancialInstitutionNumber,
      TransitNumber: details.BranchTransitNumber,
      ClientReferenceNumber: details.ClientReferenceNumber,
    },
    UpcomingTransactions: Object.values(transaction.UpcomingTransactions || {}).map(toUpcomingTransaction),
    CompletedTransactions: Object.values(transaction.CompletedTransactions || {}).map(toCompletedTransaction),
    IsLegacy: true,
  };
}
